
import { motion } from 'framer-motion';

// Shared container
const VisualContainer = ({ children }: { children: React.ReactNode }) => (
    <div className="w-full aspect-[4/3] bg-gradient-to-br from-white to-blue-50/50 rounded-xl border border-blue-100 shadow-sm overflow-hidden relative flex items-center justify-center p-3 sm:p-6 md:p-8">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#3b82f60a_1px,transparent_1px),linear-gradient(to_bottom,#3b82f60a_1px,transparent_1px)] bg-[size:16px_16px] [mask-image:radial-gradient(ellipse_at_center,black_60%,transparent_100%)] pointer-events-none" />
        {children}
    </div>
);

// Component 1: Unlimited Creativity (Prompt to Image)
export const PromptToImageVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 p-3 sm:p-6 flex flex-col relative overflow-hidden">

                {/* Prompt Bar */}
                <div className="h-10 bg-white border border-gray-200 rounded-lg shadow-sm flex items-center px-3 gap-2 mb-3 sm:mb-6 relative z-10">
                    <div className="w-4 h-4 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                    </div>
                    <div className="flex-1 overflow-hidden relative">
                        <motion.div
                            className="text-[10px] text-gray-800 whitespace-nowrap"
                            animate={{ opacity: [1, 0.5, 1] }}
                            transition={{ duration: 2, repeat: Infinity }}
                        >
                            "Futuristic city skyline at sunset, cyberpunk style..."
                        </motion.div>
                    </div>
                    <div className="px-2 py-1 bg-blue-600 text-white text-[9px] font-bold rounded">Generate</div>
                </div>

                {/* Main Canvas */}
                <div className="flex-1 bg-gray-50 rounded-lg border border-gray-100 relative overflow-hidden flex items-center justify-center">

                    {/* Placeholder Grid Animation */}
                    <div className="absolute inset-0 grid grid-cols-4 grid-rows-4">
                        {Array.from({ length: 16 }).map((_, i) => (
                            <motion.div
                                key={i}
                                className="border border-white/50 bg-gray-200"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: [0, 1, 0] }}
                                transition={{ duration: 1.5, delay: i * 0.05, repeat: Infinity }}
                            />
                        ))}
                    </div>

                    {/* Appearing Image (Abstract SVG) */}
                    <motion.div
                        className="w-full h-full absolute inset-0 bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-600 flex items-center justify-center p-8"
                        initial={{ opacity: 0, scale: 0.9, filter: "blur(10px)" }}
                        whileInView={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
                    >
                        {/* Cityscape SVG */}
                        <svg className="w-full h-full text-white mix-blend-overlay opacity-50" viewBox="0 0 100 100" preserveAspectRatio="none">
                            <polygon points="10,100 20,40 30,100" />
                            <polygon points="30,100 40,20 50,100" />
                            <polygon points="50,100 60,50 70,100" />
                            <polygon points="70,100 80,30 90,100" />
                            <circle cx="80" cy="20" r="10" />
                        </svg>
                    </motion.div>

                    {/* Progress Bar Loader Overlay */}
                    <motion.div
                        className="absolute inset-x-10 bottom-10 h-1 bg-gray-700 rounded-full overflow-hidden z-20"
                        animate={{ opacity: [1, 0, 0, 1] }}
                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
                    >
                        <motion.div
                            className="h-full bg-blue-400"
                            animate={{ width: ["0%", "100%"] }}
                            transition={{ duration: 2, ease: "linear", repeat: Infinity, repeatDelay: 2 }}
                        />
                    </motion.div>

                </div>

            </div>
        </VisualContainer>
    );
};

// Component 2: Data Visualization (Data to Chart)
export const DataVizVisual = () => {
    return (
        <VisualContainer>
            <div className="w-full max-w-[420px] h-[200px] sm:h-[320px] bg-white rounded-lg shadow-md border border-gray-100 p-0 flex relative overflow-hidden">

                {/* Left: Raw Data Table */}
                <div className="w-1/3 bg-gray-50 border-r border-gray-200 p-4 flex flex-col gap-2 relative z-10">
                    <div className="text-[10px] font-bold text-gray-500 mb-2">SOURCE DATA</div>
                    {[
                        { label: "Q1 Rev", val: "$2.4M" },
                        { label: "Q2 Rev", val: "$3.1M" },
                        { label: "Q3 Rev", val: "$4.8M" },
                        { label: "Q4 Rev", val: "$6.2M" },
                    ].map((row, i) => (
                        <div key={i} className="flex justify-between text-[9px] p-1.5 bg-white border border-gray-100 rounded shadow-sm">
                            <span className="text-gray-600">{row.label}</span>
                            <span className="font-mono font-bold text-gray-900">{row.val}</span>
                        </div>
                    ))}

                    {/* Floating "Select" Effect */}
                    <motion.div
                        className="absolute inset-0 bg-blue-500/10 pointer-events-none"
                        animate={{ top: ["20%", "80%", "20%"] }}
                        transition={{ duration: 4, repeat: Infinity }}
                    />
                </div>

                {/* Right: Chart Transformation */}
                <div className="flex-1 bg-white p-6 flex flex-col justify-center items-center relative">

                    <div className="w-full h-40 flex items-end justify-between px-2 gap-2 relative border-b border-gray-200 border-l">
                        {[30, 45, 70, 90].map((h, i) => (
                            <motion.div
                                key={i}
                                className="w-full bg-blue-500 rounded-t-sm relative group"
                                initial={{ height: 0 }}
                                whileInView={{ height: `${h}%` }}
                                transition={{ duration: 1, delay: i * 0.2 }}
                            >
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 text-[8px] font-bold text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {["2.4M", "3.1M", "4.8M", "6.2M"][i]}
                                </div>
                            </motion.div>
                        ))}

                        {/* AI Badge */}
                        <motion.div
                            className="absolute -top-6 right-0 bg-gradient-to-r from-purple-500 to-blue-500 text-white text-[8px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-sm"
                            initial={{ scale: 0 }}
                            whileInView={{ scale: 1 }}
                            transition={{ delay: 1, type: "spring" }}
                        >
                            <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9v-2h2v2zm0-4H9V7h2v5z" /></svg>
                            Generated
                        </motion.div>
                    </div>

                    <div className="w-full flex justify-between mt-2 px-2 text-[8px] text-gray-400 font-bold">
                        <span>Q1</span><span>Q2</span><span>Q3</span><span>Q4</span>
                    </div>

                </div>

                {/* Arrow Transition */}
                <div className="absolute top-1/2 left-1/3 -translate-x-1/2 -translate-y-1/2 z-20 bg-white rounded-full p-1 border border-gray-200 shadow-sm">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                </div>

            </div>
        </VisualContainer>
    );
};
